import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import MedColorDot from "@/components/MedColorDot";
import { RiskBadge } from "@/components/RiskBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { getActiveEffectSessions, getEffectSessions, addEffectEvent, deleteEffectEvent, endEffectSession, reopenEffectSession, startEffectSession, updateEffectSession, resetEffectModel, addEffectDose, removeEffectDose, getMedicationMaxDaily, getPriorDoseTotalToday, getMedicationTolerance, getLogs, getMedications } from "@/lib/api";
import { phaseAt, fmtMins, sessionDoseStack, sessionTotalDose, stackedIntensityAt, stackChartEnd, doseIntensityAt, isOralForm } from "@/lib/effectsEngine";
import { toleranceBand } from "@/lib/toleranceEngine";
import { checkInteractions, severityMeta } from "@/lib/interactions";
import { redoseWarnings } from "@/lib/redoseSafety";
import { fmtDate, doseLabel, relativeTime, toDatetimeLocal, MED_COLORS } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Activity, AlertTriangle, ChevronRight, ChevronDown, X, Zap, TrendingUp, TrendingDown, CheckCircle2, Play, Pencil, Layers, Plus, Info } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine, ReferenceDot, CartesianGrid } from "recharts";

// Stomach-fullness options for oral doses -- shared by the log sheet's
// picker and the session edit panel so the wording never diverges. The
// engine treats an unanswered picker (null) as no adjustment.
export const MEAL_OPTIONS = [
  { v: "empty", l: "Empty stomach", d: "3+ hrs since eating" },
  { v: "light", l: "Light meal", d: "1–3 hrs ago" },
  { v: "full", l: "Full meal", d: "under 1 hr ago" },
];

// Re-render on a timer so curves/labels track the clock while a session runs.
function useNow(intervalMs = 30000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

const elapsedMin = (session, now) => Math.max(0, (now - new Date(session.started_at).getTime()) / 60000);

// X-axis tick spacing, chosen to keep the label count bounded however long
// the curve runs. Short and medium sessions keep the spacing they always had;
// the longer tiers exist because genuinely long-acting substances (a
// buprenorphine curve plots a ~30 h window) would otherwise emit a dozen-plus
// ticks, which on the combined chart -- whose labels are full clock times
// rather than short "6h" offsets -- collide into an unreadable smear.
function axisStepMin(chartEnd) {
  if (chartEnd <= 150) return 30;
  if (chartEnd <= 720) return 60;
  if (chartEnd <= 1080) return 120;
  if (chartEnd <= 2160) return 360;
  return 720;
}

// True once a session's curve has actually finished playing out, tracked by
// the same client-side clock the card renders from -- independent of the
// effectSessions query's own (slower, polled) auto-complete check. Lets the
// home screen drop a finished card the instant it's felt as "gone" instead
// of waiting up to a minute for the next refetch to reclassify it.
const isSessionGone = (session, now) => {
  const stack = sessionDoseStack(session);
  const lastOffset = stack[stack.length - 1].tOffset;
  return phaseAt(elapsedMin(session, now) - lastOffset, session.profile).key === "complete";
};

const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Animates a chart's [xMin, xMax] domain and y-axis max toward new targets
// whenever they change (e.g. zooming to just the current dose's window when
// "show previous dose" is toggled off), instead of snapping. Skips the tween
// on first mount (nothing to animate from yet) and when the OS requests
// reduced motion.
function useAnimatedChartDomain(targetXDomain, targetYMax, duration = 420) {
  const [xDomain, setXDomain] = useState(targetXDomain);
  const [yMax, setYMax] = useState(targetYMax);
  const rafRef = useRef(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    const fromX = xDomain, fromY = yMax;
    const toX = targetXDomain, toY = targetYMax;
    const unchanged = fromX[0] === toX[0] && fromX[1] === toX[1] && fromY === toY;
    if (!mountedRef.current || unchanged || prefersReducedMotion()) {
      mountedRef.current = true;
      setXDomain(toX);
      setYMax(toY);
      return;
    }
    const start = performance.now();
    const tick = (now) => {
      const e = easeInOutCubic(Math.min(1, (now - start) / duration));
      setXDomain([fromX[0] + (toX[0] - fromX[0]) * e, fromX[1] + (toX[1] - fromX[1]) * e]);
      setYMax(fromY + (toY - fromY) * e);
      if (e < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetXDomain[0], targetXDomain[1], targetYMax]);

  return [xDomain, yMax];
}

function useActiveSessions() {
  return useQuery({
    queryKey: ["effectSessions", "active"],
    queryFn: getActiveEffectSessions,
    refetchInterval: 60000,
  });
}

// A compact, read-only preview of a session's curve — same solid-current /
// dotted-previous visual language as the full chart and the share image, but
// a fixed-size inline SVG (no ResponsiveContainer/ResizeObserver) so it
// animates cleanly inside the home screen's collapsible reveal.
function MiniEffectPreview({ session, now }) {
  const p = session.profile;
  if (!p) return null;
  const stack = sessionDoseStack(session);
  const newestIdx = stack.length - 1;
  const lastOffset = stack[newestIdx].tOffset;
  const maxT = stackChartEnd(p, stack) || 1;
  const points = 56;
  const series = [];
  for (let i = 0; i <= points; i++) {
    const at = (maxT * i) / points;
    const row = { t: at, intensity: doseIntensityAt(at, p, stack, newestIdx) };
    for (let k = 0; k < newestIdx; k++) row[`prev${k}`] = doseIntensityAt(at, p, stack, k);
    series.push(row);
  }
  const prevKeys = Array.from({ length: newestIdx }, (_, k) => `prev${k}`);
  const seriesMax = Math.max(100, ...series.flatMap((row) => Object.entries(row).filter(([k]) => k !== "t").map(([, v]) => v)));
  const yMax = seriesMax > 100 ? Math.min(300, Math.ceil(seriesMax / 25) * 25) : 100;
  const yTicks = yMax > 100 ? Array.from(new Set([0, 50, 100, yMax])).sort((a, b) => a - b) : [0, 50, 100];
  const xStep = maxT <= 150 ? 30 : maxT > 720 ? 120 : 60;
  const xTicks = [];
  for (let m = 0; m <= maxT; m += xStep) xTicks.push(m);

  const w = 296, h = 108;
  const marginLeft = 28, marginRight = 4, marginTop = 8, marginBottom = 16;
  const plotW = w - marginLeft - marginRight, plotH = h - marginTop - marginBottom;
  const x = (t) => marginLeft + (t / maxT) * plotW;
  const y = (v) => marginTop + plotH - (v / yMax) * plotH;
  const lineFor = (key) => series.map((pt, i) => `${i ? "L" : "M"} ${x(pt.t).toFixed(1)} ${y(pt[key]).toFixed(1)}`).join(" ");
  const line = lineFor("intensity");
  const area = `${line} L ${x(maxT).toFixed(1)} ${y(0).toFixed(1)} L ${x(0).toFixed(1)} ${y(0).toFixed(1)} Z`;
  const t = elapsedMin(session, now);
  const nowInView = t >= 0 && t <= maxT;

  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet" data-testid="mini-effect-preview" style={{ display: "block" }}>
      {yTicks.filter((v) => v > 0).map((v) => (
        <line key={`hg-${v}`} x1={marginLeft} x2={w - marginRight} y1={y(v)} y2={y(v)} stroke="hsl(var(--border))" strokeOpacity={0.55} strokeDasharray="2 4" />
      ))}
      {xTicks.map((m) => (
        <line key={`vg-${m}`} x1={x(m)} x2={x(m)} y1={marginTop} y2={y(0)} stroke="hsl(var(--border))" strokeOpacity={0.55} strokeDasharray="2 4" />
      ))}
      {lastOffset + p.onset_min <= maxT && <line x1={x(lastOffset + p.onset_min)} x2={x(lastOffset + p.onset_min)} y1={marginTop} y2={y(0)} stroke="hsl(var(--info))" strokeOpacity={0.6} strokeDasharray="3 3" />}
      {lastOffset + p.peak_min <= maxT && <line x1={x(lastOffset + p.peak_min)} x2={x(lastOffset + p.peak_min)} y1={marginTop} y2={y(0)} stroke="hsl(var(--success))" strokeOpacity={0.6} strokeDasharray="3 3" />}
      {lastOffset + p.duration_min <= maxT && <line x1={x(lastOffset + p.duration_min)} x2={x(lastOffset + p.duration_min)} y1={marginTop} y2={y(0)} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.5} strokeDasharray="3 3" />}
      {stack.slice(1).map((d, i) => (
        <line key={`rd-${i}`} x1={x(d.tOffset)} x2={x(d.tOffset)} y1={marginTop} y2={y(0)} stroke="hsl(var(--primary))" strokeOpacity={0.7} strokeDasharray="1 3" />
      ))}
      {prevKeys.map((key) => (
        <path key={key} d={lineFor(key)} fill="none" stroke="hsl(var(--muted-foreground))" strokeOpacity={0.75} strokeWidth={1.5} strokeDasharray="4 3" strokeLinejoin="round" />
      ))}
      <path d={area} fill="hsl(var(--primary))" fillOpacity={0.14} />
      <path d={line} fill="none" stroke="hsl(var(--primary))" strokeWidth={2} strokeLinejoin="round" />
      {nowInView && <line x1={x(t)} x2={x(t)} y1={marginTop} y2={y(0)} stroke="hsl(var(--warning))" strokeWidth={1.5} strokeDasharray="4 3" />}
      {yTicks.map((v) => (
        <text key={`yl-${v}`} x={marginLeft - 5} y={y(v) + 3} textAnchor="end" fontSize={9} fill="hsl(var(--muted-foreground))">{v}%</text>
      ))}
      {xTicks.map((m) => (
        <text key={`xl-${m}`} x={x(m)} y={h - 3} textAnchor="middle" fontSize={9} fill="hsl(var(--muted-foreground))">{m === 0 ? "0" : m % 60 === 0 ? `${m / 60}h` : `${m}m`}</text>
      ))}
    </svg>
  );
}

// One session's row on the home screen: the summary strip (tap to open the
// full Effects page) plus a chevron that expands a compact graph preview in
// place, so you can glance at the curve without leaving Today.
function ActiveEffectsSimpleCard({ session: s, now }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [everExpanded, setEverExpanded] = useState(false);
  const t = elapsedMin(s, now);
  const p = s.profile;
  const stack = sessionDoseStack(s);
  const lastOffset = stack[stack.length - 1].tOffset;
  // Phase and remaining track the most recent dose so a redose is
  // reflected as "coming up again" with time added back on.
  const phase = phaseAt(t - lastOffset, p);
  const intensity = Math.round(doseIntensityAt(t, p, stack, stack.length - 1));
  const chartEnd = lastOffset + p.duration_min;
  const pct = Math.min(100, (t / chartEnd) * 100);
  const remaining = Math.max(0, chartEnd - t);

  const toggleExpanded = () => {
    setExpanded((v) => !v);
    setEverExpanded(true);
  };

  return (
    <div data-testid="active-effects-card" className="card-soft p-3">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/effects")} data-testid="active-effects-card-open" className="flex items-center gap-3 flex-1 min-w-0 text-left pressable">
          <MedColorDot color={s.medication_color} size={40} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-semibold truncate">{s.medication_name}</p>
              <span className="inline-flex items-center gap-1 text-[11px] rounded-full bg-primary/12 text-primary px-2 py-0.5 font-medium"><Zap className="h-3 w-3" />{phase.label}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {phase.key === "waiting" ? `Onset ~${fmtMins(Math.max(0, p.onset_min - (t - lastOffset)))}` : `~${fmtMins(remaining)} left`}
              {sessionTotalDose(s) != null ? ` · ${doseLabel(sessionTotalDose(s), s.unit)}${(s.redoses || []).length ? " total" : ""}` : ""}
            </p>
            <div className="mt-1.5 h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full bg-primary transition-[width] duration-1000" style={{ width: `${Math.max(3, pct)}%` }} />
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="font-display text-xl font-semibold leading-none">{intensity}%</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">intensity</p>
          </div>
        </button>
        <button
          onClick={toggleExpanded}
          aria-label={expanded ? "Hide graph preview" : "Show graph preview"}
          aria-expanded={expanded}
          data-testid="active-effects-preview-toggle"
          className="pressable h-9 w-9 rounded-full flex items-center justify-center text-muted-foreground shrink-0"
        >
          <ChevronDown className={cn("h-5 w-5 transition-transform duration-300", expanded && "rotate-180")} />
        </button>
      </div>
      <div className={cn("grid transition-[grid-template-rows] duration-300 ease-in-out", expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}>
        <div className="overflow-hidden">
          <div className={cn("pt-3 mt-2 border-t border-border transition-opacity duration-200", expanded ? "opacity-100" : "opacity-0")}>
            {everExpanded && <MiniEffectPreview session={s} now={now} />}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- simplified card (home screen) ----
export function ActiveEffectsSimple() {
  const now = useNow(30000);
  const { data: sessions = [] } = useActiveSessions();
  // Drop a session from the home screen the instant its curve is done,
  // rather than leaving a "Gone" card sitting there until the next poll
  // reclassifies it (up to a minute later).
  const visible = useMemo(() => sessions.filter((s) => !isSessionGone(s, now)), [sessions, now]);
  if (!visible.length) return null;
  return (
    <div className="space-y-2.5" data-testid="active-effects-simple">
      {visible.map((s) => <ActiveEffectsSimpleCard key={s.id} session={s} now={now} />)}
    </div>
  );
}

// ---- detailed view (Effects page) ----
const FEEDBACK = [
  { kind: "onset", label: "Feeling it", icon: TrendingUp },
  { kind: "peak", label: "Peaking", icon: Zap },
  { kind: "wearing_off", label: "Wearing off", icon: TrendingDown },
  { kind: "gone", label: "Gone", icon: CheckCircle2 },
];

function SessionDetail({ session, now }) {
  const qc = useQueryClient();
  const [intensityInput, setIntensityInput] = useState([5]);
  const [editing, setEditing] = useState(false);
  const [editWhen, setEditWhen] = useState("");
  const [editDose, setEditDose] = useState("");
  const [editMeal, setEditMeal] = useState(null);
  const [showPrevious, setShowPrevious] = useState(false);
  const [redosing, setRedosing] = useState(false);
  const [redoseAmt, setRedoseAmt] = useState("");
  const [redoseWhen, setRedoseWhen] = useState("");
  // Typical max daily dose (from the catalog) for the redose guardrails.
  const { data: maxDaily = null } = useQuery({
    queryKey: ["medMaxDaily", session.medication_id],
    queryFn: () => getMedicationMaxDaily(session.medication_id),
  });
  // Tolerance as it stands *now*. The session's own profile carries a
  // tolerance snapshot too, but that one is frozen at the moment the session
  // started and deliberately excludes the session's own dose -- correct for
  // dampening this dose's curve, wrong for a meter labelled "Tolerance",
  // which would then disagree with the identical meter in the log sheet and
  // on the medication page (both of which read live). Same query key as those,
  // so all three share one cache entry and can't drift apart.
  const { data: liveTolerance } = useQuery({
    queryKey: ["medicationTolerance", session.medication_id],
    queryFn: () => getMedicationTolerance(session.medication_id),
    staleTime: 0,
    refetchInterval: 60000,
  });

  // Anything of this medication already taken earlier today outside this
  // session, so a max-DAILY-dose check actually covers the day.
  const { data: priorToday = 0 } = useQuery({
    queryKey: ["priorDoseToday", session.medication_id, session.id],
    queryFn: () => getPriorDoseTotalToday(session.medication_id, { excludeSessionId: session.id }),
    staleTime: 0,
  });
  const t = elapsedMin(session, now);
  const p = session.profile;
  // The session's effect is the sum of its primary dose and any redoses
  // (stacked curves). tOffset 0 for the primary; a redose's curve is shifted
  // to when it was taken and scaled by its amount vs. the primary.
  const stack = useMemo(() => sessionDoseStack(session), [session]);
  const redoses = session.redoses || [];
  const newestIdx = stack.length - 1;
  const lastOffset = stack[newestIdx].tOffset;
  // Phase tracks the most recent dose so a redose reads as "coming up" again.
  const phase = phaseAt(t - lastOffset, p);
  // The filled curve is the dose you're currently on — earlier doses are kept
  // out of it entirely rather than folded in, so a redose reads as its own
  // clean curve instead of a hump carried over from the dose before it. Each
  // superseded dose is still available on demand via "Show previous dose",
  // drawn as its own dotted line. For a session with no redose this is
  // exactly the single dose's curve, i.e. unchanged.
  const plottedAt = useCallback((mins) => doseIntensityAt(mins, p, stack, newestIdx), [p, stack, newestIdx]);
  // Curve, axis, gridlines, dots and the written number all read off the same
  // value so the graph never disagrees with the label.
  const intensity = Math.round(plottedAt(t));
  const supersededCount = Math.max(0, stack.length - 1);
  const series = useMemo(() => {
    const end = stackChartEnd(p, stack);
    const points = 96;
    const rows = [];
    for (let k = 0; k <= points; k++) {
      const at = (end * k) / points;
      const row = { t: Math.round(at), intensity: doseIntensityAt(at, p, stack, newestIdx) };
      if (showPrevious) for (let i = 0; i < supersededCount; i++) row[`dose${i}`] = doseIntensityAt(at, p, stack, i);
      rows.push(row);
    }
    return rows;
  }, [p, stack, showPrevious, supersededCount, newestIdx]);
  const startMs = new Date(session.started_at).getTime();
  const clockAt = (mins) => fmtDate(new Date(startMs + mins * 60000), "h:mm a");
  const given = (kind) => session.events?.some((e) => e.kind === kind);

  // Y-axis fits the highest point the stacked curve actually reaches (a strong
  // dose, or overlapping redoses, can exceed 100% — "your typical peak").
  const chartEnd = stackChartEnd(p, stack);
  // Fit the axis to every plotted line — a re-shown previous dose can sit
  // above the handed-off total at points where it alone was still strong.
  const seriesMax = Math.max(100, ...series.flatMap((pt) => Object.entries(pt).filter(([k]) => k !== "t").map(([, v]) => v)));
  const strong = seriesMax > 100;
  const targetYMax = strong ? Math.min(300, Math.ceil(seriesMax / 25) * 25) : 100;
  const yTicks = useMemo(
    () => (strong ? Array.from(new Set([0, 50, 100, targetYMax])).sort((a, b) => a - b) : [0, 25, 50, 75, 100]),
    [strong, targetYMax]
  );
  const yGrid = yTicks.filter((v) => v > 0);

  // Where "0" sits on the time axis. With previous doses hidden the chart is
  // zoomed to the current dose alone, so measuring from session start would
  // label that dose's onset as (say) 6 h -- time that belongs to a curve
  // that isn't even drawn. Rebasing on the dose being shown makes the axis
  // read as time since *this* dose, and reverts to session-relative time when
  // the earlier doses come back into view.
  const timeOrigin = showPrevious ? 0 : lastOffset;

  // Hourly gridline positions across the visible span (denser for short
  // sessions), anchored to that origin so the labelled hours land on round
  // numbers rather than wherever the redose happened to fall.
  const hourTicks = useMemo(() => {
    const step = axisStepMin(chartEnd - timeOrigin);
    const out = [];
    for (let m = timeOrigin; m <= chartEnd; m += step) out.push(m);
    return out;
  }, [chartEnd, timeOrigin]);
  const fmtAxis = useCallback((m) => {
    const rel = Math.round(m - timeOrigin);
    return rel === 0 ? "0" : rel % 60 === 0 ? `${rel / 60}h` : `${rel}m`;
  }, [timeOrigin]);

  // Zoom the visible window to just the current dose when its predecessor is
  // hidden — nothing interesting happens before it (the plotted curve is flat
  // zero there) — and zoom back out to the full timeline when it's shown, so
  // the reappearing dotted line has room. Animated rather than snapped.
  const targetXDomain = useMemo(() => [showPrevious ? 0 : lastOffset, chartEnd], [showPrevious, lastOffset, chartEnd]);
  const [xDomain, yMax] = useAnimatedChartDomain(targetXDomain, targetYMax);
  const visibleHourTicks = useMemo(
    () => hourTicks.filter((m) => m >= xDomain[0] - 0.5 && m <= xDomain[1] + 0.5),
    [hourTicks, xDomain]
  );
  const inView = (x) => x >= xDomain[0] - 0.5 && x <= xDomain[1] + 0.5;
  // Same "fixed final set, filtered to what's currently in view" treatment
  // for the y-axis while its own max animates.
  const visibleYTicks = useMemo(() => yTicks.filter((v) => v <= yMax + 0.5), [yTicks, yMax]);
  const visibleYGrid = visibleYTicks.filter((v) => v > 0);

  // Feedback events plotted on the chart: phase reports sit on the (stacked)
  // curve, intensity reports at the strength the user actually felt (0-10 → 0-100%).
  const eventDots = useMemo(() => (session.events || []).map((e) => {
    const m = Math.max(0, (new Date(e.t).getTime() - startMs) / 60000);
    if (m > chartEnd) return null;
    const y = e.kind === "intensity" && e.intensity != null ? e.intensity * 10 : plottedAt(m);
    return { key: e.id, x: Math.round(m), y: Math.round(y), kind: e.kind };
  }).filter(Boolean), [session.events, startMs, chartEnd, plottedAt]);

  // Vertical markers where each redose was taken (skip the primary at 0).
  const redoseMarks = stack.slice(1).map((d) => Math.round(d.tOffset));

  // mealModel rides along: completing a session with feedback is exactly
  // when the learned meal factors can change.
  const invalidate = () => ["effectSessions", "mealModel"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  // Redosing (and undoing a redose) logs/unlogs a real dose under the hood,
  // which decrements/restores inventory -- so it needs the same broad
  // invalidation as any other dose-logging action (see Today.jsx, QuickLogSheet.jsx),
  // not just the effectSessions cache.
  const invalidateDoseChange = () => {
    invalidate();
    ["today", "logs", "analytics", "inventory", "medications", "medication", "activeSubstances", "interactions", "effectivenessSuggestion", "medicationTolerance", "priorDoseToday"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };
  // "Undo" on the toast reopens the session — reverses both the completion
  // and (if nothing newer has touched the model since) the training it
  // triggered. Wrapped so the error from a stale/unsafe undo surfaces clearly.
  const undoComplete = () => reopenEffectSession(session.id).then(invalidate).catch((e) => toast.error(e?.message || "Could not undo"));

  const feedback = useMutation({
    mutationFn: (payload) => addEffectEvent(session.id, payload),
    onSuccess: (s, vars) => {
      invalidate();
      if (vars.kind === "gone") {
        toast.success("Session complete — your model was updated", { action: { label: "Undo", onClick: undoComplete } });
      } else {
        toast.success("Noted — this tunes your personal curve");
      }
    },
    onError: (e) => toast.error(e?.message || "Could not record feedback"),
  });
  // Removes one recorded event ("editing" a session mid-flight) — for
  // anything but the very last tap, more precise than a full Undo.
  const undoEvent = useMutation({
    mutationFn: (eventId) => deleteEffectEvent(session.id, eventId),
    onSuccess: () => { invalidate(); toast.success("Removed"); },
    onError: (e) => toast.error(e?.message || "Could not remove"),
  });
  const finish = useMutation({
    mutationFn: (opts) => endEffectSession(session.id, opts),
    onSuccess: (_s, opts) => {
      invalidate();
      toast.success(opts?.discard ? "Session discarded" : "Session ended", { action: { label: "Undo", onClick: undoComplete } });
    },
    onError: () => toast.error("Could not end session"),
  });
  const edit = useMutation({
    mutationFn: (patch) => updateEffectSession(session.id, patch),
    onSuccess: () => { invalidate(); setEditing(false); toast.success("Session updated"); },
    onError: (e) => toast.error(e?.message || "Could not update session"),
  });
  const reset = useMutation({
    mutationFn: () => resetEffectModel(session.medication_id),
    onSuccess: () => { invalidate(); toast.success("Model reset — back to typical values"); },
    onError: () => toast.error("Could not reset the model"),
  });
  const redose = useMutation({
    mutationFn: (payload) => addEffectDose(session.id, payload),
    onSuccess: () => { invalidateDoseChange(); setRedosing(false); setRedoseAmt(""); toast.success("Redose added — the curve now stacks it on"); },
    onError: (e) => toast.error(e?.message || "Could not add redose"),
  });
  const removeDose = useMutation({
    mutationFn: (doseId) => removeEffectDose(session.id, doseId),
    onSuccess: () => { invalidateDoseChange(); toast.success("Redose removed"); },
    onError: (e) => toast.error(e?.message || "Could not remove redose"),
  });

  const openRedose = () => {
    setRedoseAmt(session.dose != null ? String(session.dose) : "");
    setRedoseWhen(toDatetimeLocal());
    setRedosing(true);
  };
  // Live safety check for the redose being composed (too-soon / over-max).
  const redoseSafety = useMemo(() => {
    if (!redosing) return { warnings: [] };
    const at = redoseWhen && !isNaN(new Date(redoseWhen).getTime()) ? new Date(redoseWhen).toISOString() : null;
    return redoseWarnings(session, { amount: redoseAmt, at }, maxDaily, session.unit, priorToday);
  }, [redosing, redoseWhen, redoseAmt, session, maxDaily, priorToday]);
  const saveRedose = () => {
    const when = new Date(redoseWhen);
    if (!redoseWhen || isNaN(when.getTime())) { toast.error("Enter a valid date and time"); return; }
    if (when.getTime() > Date.now() + 60000) { toast.error("Redose time can't be in the future"); return; }
    const payload = { at: when.toISOString() };
    if (redoseAmt !== "") {
      const v = Number(redoseAmt);
      if (!isFinite(v) || v < 0) { toast.error("Enter a valid dose"); return; }
      payload.amount = v;
    }
    redose.mutate(payload);
  };

  const openEdit = () => {
    setEditWhen(toDatetimeLocal(session.started_at));
    setEditDose(session.dose != null ? String(session.dose) : "");
    setEditMeal(session.last_meal || null);
    setEditing(true);
  };
  const saveEdit = () => {
    const d = new Date(editWhen);
    if (!editWhen || isNaN(d.getTime())) { toast.error("Enter a valid date and time"); return; }
    if (d.getTime() > Date.now() + 60000) { toast.error("Start time can't be in the future"); return; }
    const patch = { started_at: d.toISOString() };
    if (editDose !== "") {
      const v = Number(editDose);
      if (!isFinite(v) || v < 0) { toast.error("Enter a valid dose"); return; }
      patch.dose = v;
    }
    if ((editMeal || null) !== (session.last_meal || null)) patch.last_meal = editMeal;
    edit.mutate(patch);
  };

  return (
    <div className="card-soft p-4" data-testid="effect-session-detail">
      {/* Header: title row, then the live phase chip on its own row so the
          subtitle never wraps mid-time on narrow screens. */}
      <div className="flex items-center gap-3">
        <MedColorDot color={session.medication_color} size={44} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{session.medication_name}</p>
          <p className="text-xs text-muted-foreground truncate" data-testid="effect-session-subtitle">
            {/* Headline dose is the whole session's total: a redose raises
                what was actually taken, so an 8000 mg session with a 1000 mg
                redose reads 9000 mg, not the opening dose forever. */}
            {sessionTotalDose(session) != null
              ? `${doseLabel(sessionTotalDose(session), session.unit)}${(session.redoses || []).length ? ` total · ${1 + session.redoses.length} doses` : ""} · `
              : ""}
            started {relativeTime(session.started_at)}
          </p>
        </div>
        <button onClick={openEdit} aria-label="Edit session" data-testid="effect-edit-button" className="pressable h-9 w-9 rounded-full hover:bg-muted flex items-center justify-center text-primary shrink-0"><Pencil className="h-4 w-4" /></button>
      </div>
      <div className="mt-2">
        <span className="inline-flex items-center gap-1 text-[11px] rounded-full bg-primary/12 text-primary px-2.5 py-1 font-medium"><Zap className="h-3 w-3" />{phase.key === "complete" ? "Gone" : `${phase.label} · ${intensity}% intensity`}</span>
      </div>
      <ToleranceNote tolerance={liveTolerance} />

      {editing && (
        <div className="mt-3 rounded-xl border border-border p-3 animate-rise" data-testid="effect-edit-panel">
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Taken at</Label>
              <Input type="datetime-local" value={editWhen} max={toDatetimeLocal()} onChange={(e) => setEditWhen(e.target.value)} className="h-11 rounded-xl mt-1" data-testid="effect-edit-when" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Dose{session.unit ? ` (${session.unit})` : ""}</Label>
              <Input type="number" value={editDose} onChange={(e) => setEditDose(e.target.value)} className="h-11 rounded-xl mt-1" data-testid="effect-edit-dose" />
            </div>
            {isOralForm(session.medication_form) && (
              <div>
                <Label className="text-xs text-muted-foreground">Last meal before this dose</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {MEAL_OPTIONS.map((m) => (
                    <button key={m.v} onClick={() => setEditMeal(editMeal === m.v ? null : m.v)} data-testid={`effect-edit-meal-${m.v}`}
                      className={cn("pressable rounded-xl border py-2 px-1 text-center", editMeal === m.v ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-foreground")}>
                      <span className="block text-xs font-medium leading-tight">{m.l}</span>
                      <span className={cn("block text-[10px] leading-tight mt-0.5", editMeal === m.v ? "text-primary-foreground/80" : "text-muted-foreground")}>{m.d}</span>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1.5">Correcting this reshapes the predicted curve.</p>
              </div>
            )}
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" className="flex-1 rounded-xl" onClick={saveEdit} disabled={edit.isPending} data-testid="effect-edit-save">Save</Button>
            <Button size="sm" variant="secondary" className="flex-1 rounded-xl" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">Moving the start time re-anchors the whole curve — useful when you actually took the dose earlier.</p>
        </div>
      )}

      {supersededCount > 0 && (
        <div className="mt-3 flex justify-end">
          <button
            onClick={() => setShowPrevious((s) => !s)}
            data-testid="effect-show-previous-button"
            className="text-[11px] font-medium text-primary"
          >
            {showPrevious ? "Hide previous dose" : `Show previous dose${supersededCount > 1 ? "s" : ""}`}
          </button>
        </div>
      )}

      <div className="mt-3 -mx-1">
        <ResponsiveContainer width="100%" height={160}>
          <AreaChart data={series} margin={{ left: 0, right: 8, top: 6, bottom: 0 }}>
            <defs>
              <linearGradient id={`fx-${session.id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.55} strokeDasharray="2 4" horizontalValues={visibleYGrid} verticalValues={visibleHourTicks} />
            <XAxis dataKey="t" type="number" domain={xDomain} allowDataOverflow ticks={visibleHourTicks} interval={0} tickFormatter={fmtAxis} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} animationDuration={0} />
            <YAxis domain={[0, yMax + 5]} allowDataOverflow ticks={visibleYTicks} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 9 }} width={38} tickMargin={4} tickLine={false} axisLine={false} animationDuration={0} />
            <Tooltip
              contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }}
              formatter={(v) => [`${Math.round(v)}%`, "Intensity"]}
              labelFormatter={(m) => `${fmtMins(m - timeOrigin)} after ${timeOrigin > 0 ? "this dose" : "dose"} · ${clockAt(m)}`}
            />
            {/* predicted phase boundaries — hidden once zoomed past them */}
            {inView(lastOffset + p.onset_min) && <ReferenceLine x={lastOffset + p.onset_min} stroke="hsl(var(--info))" strokeOpacity={0.6} strokeDasharray="3 3" />}
            {inView(lastOffset + p.peak_min) && <ReferenceLine x={lastOffset + p.peak_min} stroke="hsl(var(--success))" strokeOpacity={0.6} strokeDasharray="3 3" />}
            {inView(lastOffset + p.duration_min) && <ReferenceLine x={lastOffset + p.duration_min} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.5} strokeDasharray="3 3" />}
            {/* redose markers — where an additional dose was stacked on */}
            {redoseMarks.filter(inView).map((m, i) => (
              <ReferenceLine
                key={`rd-${i}`}
                x={m}
                stroke="hsl(var(--primary))"
                strokeOpacity={0.7}
                strokeDasharray="1 3"
                // Unlabelled when it sits on the axis origin: zoomed to this
                // dose, "0" already means "this dose", so the tag says nothing
                // and only collides with the "now" marker beside it.
                label={m - timeOrigin > (xDomain[1] - xDomain[0]) * 0.04
                  ? { value: "+dose", position: "insideTopLeft", fontSize: 8, fill: "hsl(var(--primary))" }
                  : undefined}
              />
            ))}
            <Area type="monotone" dataKey="intensity" stroke="hsl(var(--primary))" strokeWidth={2.5} fill={`url(#fx-${session.id})`} dot={false} isAnimationActive={false} />
            {/* each superseded dose on its own, when asked for — line only,
                so the current dose stays the one filled curve. Zooming back
                out (below) gives this room across the whole chart. */}
            {showPrevious && Array.from({ length: supersededCount }, (_, i) => (
              <Area key={`dose-${i}`} type="monotone" dataKey={`dose${i}`} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.75} strokeWidth={1.5} strokeDasharray="4 3" fill="none" fillOpacity={0} dot={false} isAnimationActive={false} />
            ))}
            {/* the user's own feedback, plotted where it happened */}
            {eventDots.filter((d) => inView(d.x)).map((d) => (
              <ReferenceDot key={d.key} x={d.x} y={d.y} r={4} fill={d.kind === "intensity" ? "hsl(var(--warning))" : "hsl(var(--primary))"} stroke="hsl(var(--card))" strokeWidth={1.5} />
            ))}
            {/* label suppressed while the line hugs the left edge, where it
                would collide with the 100% axis tick */}
            {inView(t) && <ReferenceLine x={Math.round(t)} stroke="hsl(var(--warning))" strokeDasharray="4 3" strokeWidth={2} label={t - xDomain[0] > (xDomain[1] - xDomain[0]) * 0.08 ? { value: "now", position: "insideTopRight", fontSize: 9, fill: "hsl(var(--warning))" } : undefined} />}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-1 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-xl bg-muted/40 py-1.5"><p className="text-[10px] text-muted-foreground">Onset</p><p className="text-xs font-medium">~{clockAt(lastOffset + p.onset_min)}</p></div>
        <div className="rounded-xl bg-muted/40 py-1.5"><p className="text-[10px] text-muted-foreground">Peak</p><p className="text-xs font-medium">~{clockAt(lastOffset + p.peak_min)}</p></div>
        <div className="rounded-xl bg-muted/40 py-1.5"><p className="text-[10px] text-muted-foreground">Ends</p><p className="text-xs font-medium">~{clockAt(lastOffset + p.duration_min)}</p></div>
      </div>

      <div className="flex items-center justify-between gap-2 mt-2">
        <p className="text-[11px] text-muted-foreground">
          {p.learned
            ? `Personalized from ${p.samples} tracked ${p.samples === 1 ? "session" : "sessions"} (${p.confidence} confidence).`
            : "Using typical values for now — your feedback below teaches the tracker your metabolism."}
        </p>
        {p.learned && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="text-[11px] font-medium text-primary shrink-0" data-testid="effect-reset-model-button">Reset</button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reset the learned model for {session.medication_name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Everything the tracker has learned about your onset, peak and duration for this medication is forgotten, and predictions go back to typical values. Future feedback starts teaching it again from scratch.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => reset.mutate()} className="bg-destructive text-destructive-foreground" data-testid="effect-reset-model-confirm">Reset model</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <div className="mt-3 grid grid-cols-4 gap-2">
        {FEEDBACK.map((f) => {
          const Icon = f.icon;
          const done = given(f.kind);
          return (
            <button
              key={f.kind}
              disabled={done || feedback.isPending}
              onClick={() => feedback.mutate({ kind: f.kind })}
              data-testid={`effect-feedback-${f.kind}`}
              className={`pressable rounded-xl border py-2 flex flex-col items-center gap-1 text-[11px] font-medium ${done ? "bg-accent border-primary text-muted-foreground opacity-60" : "bg-card border-border"}`}
            >
              <Icon className="h-4 w-4" />{f.label}
            </button>
          );
        })}
      </div>

      <div className="mt-3">
        <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>How strong does it feel?</span><span>{intensityInput[0]}/10</span></div>
        <Slider value={intensityInput} onValueChange={setIntensityInput} onValueCommit={(v) => feedback.mutate({ kind: "intensity", intensity: v[0] })} min={0} max={10} step={1} data-testid="effect-intensity-slider" />
      </div>

      {/* Recorded feedback, most recent first — tap × to fix a specific wrong
          entry, or "Undo last" for the common case of the last tap. */}
      {session.events?.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs text-muted-foreground">Your feedback</p>
            <button
              onClick={() => undoEvent.mutate(session.events[session.events.length - 1].id)}
              disabled={undoEvent.isPending}
              data-testid="effect-undo-last-button"
              className="text-[11px] font-medium text-primary"
            >
              Undo last
            </button>
          </div>
          <div className="space-y-1">
            {[...session.events].reverse().map((e) => {
              const meta = FEEDBACK.find((f) => f.kind === e.kind);
              const mins = Math.max(0, (new Date(e.t).getTime() - startMs) / 60000);
              const label = e.kind === "intensity" ? `Intensity ${e.intensity}/10` : (meta?.label || e.kind);
              return (
                <div key={e.id} className="flex items-center justify-between rounded-lg bg-muted/30 pl-2.5 pr-1 py-1" data-testid="effect-event-row">
                  <span className="text-xs">{label} <span className="text-muted-foreground">· {fmtMins(mins)} in</span></span>
                  <button
                    onClick={() => undoEvent.mutate(e.id)}
                    disabled={undoEvent.isPending}
                    aria-label={`Remove ${label}`}
                    data-testid="effect-event-delete"
                    className="pressable h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Redose — stack another dose onto this session instead of starting a
          separate one. Taken doses are listed with remove buttons. */}
      <div className="mt-3">
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Redose</p>
          {!redosing && (
            <button onClick={openRedose} data-testid="effect-redose-button" className="inline-flex items-center gap-1 text-[11px] font-medium text-primary">
              <Plus className="h-3.5 w-3.5" />Add a dose
            </button>
          )}
        </div>
        {redoses.length > 0 && (
          <div className="space-y-1 mt-1.5">
            {redoses.map((r) => {
              const mins = Math.max(0, (new Date(r.at).getTime() - startMs) / 60000);
              return (
                <div key={r.id} className="flex items-center justify-between rounded-lg bg-muted/30 pl-2.5 pr-1 py-1" data-testid="effect-redose-row">
                  <span className="text-xs">{r.amount != null ? doseLabel(r.amount, r.unit || session.unit) : "Dose"} <span className="text-muted-foreground">· {fmtMins(mins)} in</span></span>
                  <button onClick={() => removeDose.mutate(r.id)} disabled={removeDose.isPending} aria-label="Remove redose" data-testid="effect-redose-delete" className="pressable h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-destructive"><X className="h-3.5 w-3.5" /></button>
                </div>
              );
            })}
          </div>
        )}
        {redosing && (
          <div className="mt-2 rounded-xl border border-border p-3 animate-rise" data-testid="effect-redose-panel">
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Amount{session.unit ? ` (${session.unit})` : ""}</Label>
                <Input type="number" value={redoseAmt} onChange={(e) => setRedoseAmt(e.target.value)} className="h-11 rounded-xl mt-1" data-testid="effect-redose-amount" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Taken at</Label>
                <Input type="datetime-local" value={redoseWhen} max={toDatetimeLocal()} onChange={(e) => setRedoseWhen(e.target.value)} className="h-11 rounded-xl mt-1" data-testid="effect-redose-when" />
              </div>
            </div>
            <RedoseSafetyBox warnings={redoseSafety.warnings} unit={session.unit} />
            <div className="flex gap-2 mt-3">
              <Button size="sm" className={`flex-1 rounded-xl ${redoseSafety.warnings.length ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}`} onClick={saveRedose} disabled={redose.isPending} data-testid="effect-redose-save">{redoseSafety.warnings.length ? "Add anyway" : "Add dose"}</Button>
              <Button size="sm" variant="secondary" className="flex-1 rounded-xl" onClick={() => setRedosing(false)}>Cancel</Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">The redose stacks onto whatever is still active — the curve sums both. Redosed sessions don't train your timing model, since the stacked timing isn't a clean single-dose reading.</p>
          </div>
        )}
      </div>

      <div className="mt-3 flex gap-2">
        <Button variant="secondary" size="sm" className="flex-1 rounded-xl" onClick={() => finish.mutate({ learn: true })} data-testid="effect-end-button">End session</Button>
        <Button variant="ghost" size="sm" className="rounded-xl text-muted-foreground" onClick={() => finish.mutate({ discard: true })} data-testid="effect-discard-button"><X className="h-4 w-4 mr-1" />Discard</Button>
      </div>
    </div>
  );
}

// ---- redose safety guardrails ----
// The tolerance meter, shown wherever a dose is being considered: the effects
// graph, the log sheet's dose preview, and the medication page. One shared
// component precisely so the same number and the same words appear in all
// three places.
//
// Two distinct states, deliberately different in tone: a routine, muted note
// for ordinary built-up tolerance (expected, not alarming), and a caution box
// -- same visual language as RedoseSafetyBox -- when tolerance looks like it
// has faded across a gap, because that's the one that matters for safety (a
// usual dose can hit much harder than expected). When it has faded, a marker
// at the recent peak level shows "it used to be this high, now it's back down
// to here" visually, not just in prose.
//
// Three things about this number are easy to misread, so all three are stated
// on the face of the component rather than implied:
//
//   - The percentage is *how far along this substance's own tolerance range*
//     you are, not how much weaker things feel. They aren't the same: each
//     substance has a ceiling on how much tolerance can blunt it (opioids
//     ~60%, antihistamines ~30%), so 91% tolerance on an opioid means doses
//     land roughly 55% weaker, not 91% weaker. The "% weaker" figure is what
//     people actually mean by "my tolerance", so that's what's shown next to
//     the band; the raw level is relegated to the bar and its scale caption.
//   - The bar's scale is labelled ("none" -> "most this can build") instead of
//     being left as an unmarked 0-100, which reads as a share of something.
//   - It is *not* what shrinks the effects curve above it. That curve is drawn
//     against the user's own usual dose, which carries the same tolerance, so
//     it cancels out there -- a normal dose peaks at 100% however tolerant you
//     are. The copy used to claim the opposite, which stopped being true once
//     the curve was rebaselined against the user's own usual dose.
export function ToleranceNote({ tolerance, label = "Tolerance" }) {
  const [showInfo, setShowInfo] = useState(false);
  if (!tolerance) return null;
  const { level, faded, recentPeakLevel, daysSinceLast, maxDampening } = tolerance;
  if (!faded && level < 0.15) return null;
  const pct = Math.round(level * 100);
  const peakPct = faded && recentPeakLevel != null ? Math.round(recentPeakLevel * 100) : null;
  // How much weaker doses actually land -- what people mean when they ask what
  // their tolerance "is", and the same quantity the dose preview reports as
  // factors.toleranceDampening, so the two surfaces never disagree.
  const weakerPct = maxDampening != null ? Math.round(level * maxDampening * 100) : null;

  return (
    <div className="mt-2" data-testid="tolerance-meter">
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        {/* `label` is dropped where the surrounding card already says
            "Tolerance", so the word doesn't appear twice in a row. */}
        <button type="button" onClick={() => setShowInfo((v) => !v)} className="flex items-center gap-1" aria-label="What does tolerance mean?" data-testid="tolerance-info-toggle">
          {label ? `${label}${faded ? " (faded)" : ""} ` : faded ? "Faded " : ""}<Info className="h-3 w-3" />
        </button>
        {/* Faded leads with the drop rather than the (now tiny) "% weaker",
            which next to the caution box below would read as "nothing to see
            here" -- the opposite of the point. */}
        <span className={faded ? "font-medium text-[hsl(var(--warning))]" : "font-medium text-foreground"} data-testid="tolerance-pct">
          {faded && peakPct != null
            ? `${toleranceBand(level)} now · was ${toleranceBand(recentPeakLevel).toLowerCase()}`
            : `${toleranceBand(level)}${weakerPct != null ? ` · doses land ~${weakerPct}% weaker` : ` · ${pct}%`}`}
        </span>
      </div>
      <div className="relative mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-[width] duration-500 ${faded ? "bg-[hsl(var(--warning))]" : "bg-primary"}`}
          style={{ width: `${pct}%` }}
        />
        {peakPct != null && peakPct > pct && (
          <div className="absolute inset-y-0 w-0.5 bg-muted-foreground/60" style={{ left: `${peakPct}%` }} title={`Recent peak tolerance: ~${peakPct}% of this substance's range`} />
        )}
      </div>
      {/* The bar's scale, spelled out -- an unlabelled 0-100% bar is what made
          this number ambiguous in the first place. */}
      <div className="mt-0.5 flex items-center justify-between text-[9px] text-muted-foreground/70" data-testid="tolerance-scale">
        <span>no tolerance</span>
        <span>{pct}% of the most this substance can build</span>
      </div>
      {faded ? (
        <div className="mt-2 rounded-xl border border-[hsl(var(--warning-border))] bg-[hsl(var(--warning-surface))] p-2.5" data-testid="tolerance-faded-note">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-[hsl(var(--warning))]" />
            <p className="text-[11px] text-muted-foreground leading-snug">
              <span className="font-semibold text-[hsl(var(--warning))]">Tolerance may have faded — </span>
              it's been {Math.round(daysSinceLast)} day{Math.round(daysSinceLast) === 1 ? "" : "s"} since your last dose, so your tolerance has dropped from about {peakPct}% to {pct}% of this substance's range. The amount you used to take could now hit stronger than you're used to — consider starting lower.
            </p>
          </div>
        </div>
      ) : (
        <p className="mt-1.5 text-[11px] text-muted-foreground leading-snug" data-testid="tolerance-note">
          {weakerPct != null
            ? `Your recent use has built up tolerance, so a dose now lands about ${weakerPct}% weaker than the same dose would with no tolerance.`
            : "Your recent use has built up tolerance, so a dose now lands weaker than the same dose would with no tolerance."}
        </p>
      )}
      {showInfo && (
        <div className="mt-1.5 space-y-1 text-[11px] text-muted-foreground leading-snug animate-rise" data-testid="tolerance-info-text">
          <p>
            <span className="font-medium text-foreground">What it measures.</span>{" "}
            How much your recent use has blunted this substance for you. Every substance has a ceiling on how far tolerance can go — opioids can blunt effects by roughly 60%, antihistamines by roughly 30% — and the bar shows how close to that ceiling you are, not how much weaker doses feel.
            {weakerPct != null && ` At ${pct}% of the range, doses land about ${weakerPct}% weaker — not ${pct}% weaker.`}
          </p>
          <p>
            <span className="font-medium text-foreground">Where it comes from.</span>{" "}
            Your own logged doses: how much you take per day (not how many separate times you dose), weighted toward the last few days and fading as you go without.
          </p>
          <p>
            <span className="font-medium text-foreground">What it does not change.</span>{" "}
            Predicted effect and the effects curve are measured against your own usual dose, which already carries this same tolerance — so it cancels out there and a usual dose still reads 100%. Tolerance is shown separately here because it's the part that changes what a given amount is worth.
          </p>
        </div>
      )}
    </div>
  );
}

function RedoseSafetyBox({ warnings, unit }) {
  if (!warnings?.length) return null;
  const severe = warnings.some((w) => w.severity === "severe");
  const line = (w) => {
    if (w.type === "too_soon") return `The previous dose likely hasn't peaked yet (${fmtMins(w.minsSinceLast)} in, typically peaks ~${fmtMins(w.peakMin)}). Waiting until you feel the full effect avoids accidental over-stacking.`;
    if (w.type === "over_max") return `This brings the session total to ${w.cumulative}${unit ? ` ${unit}` : ""} — above the typical maximum of ${w.maxDaily}${unit ? ` ${unit}` : ""}.`;
    if (w.type === "near_max") return `This brings the session total to ${w.cumulative}${unit ? ` ${unit}` : ""}, near the typical maximum of ${w.maxDaily}${unit ? ` ${unit}` : ""}.`;
    return "";
  };
  return (
    <div className={`mt-3 rounded-xl border p-2.5 ${severe ? "border-destructive/40 bg-destructive/5" : "border-[hsl(var(--warning-border))] bg-[hsl(var(--warning-surface))]"}`} data-testid="redose-safety-box">
      <div className="flex items-start gap-2">
        <AlertTriangle className={`h-4 w-4 shrink-0 mt-0.5 ${severe ? "text-destructive" : "text-[hsl(var(--warning))]"}`} />
        <div className="min-w-0">
          <p className={`text-xs font-semibold ${severe ? "text-destructive" : "text-[hsl(var(--warning))]"}`}>{severe ? "Dose safety" : "Heads up"}</p>
          <ul className="mt-1 space-y-1">
            {warnings.map((w, i) => <li key={i} className="text-[11px] text-muted-foreground leading-snug">{line(w)}</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ---- interaction warnings (concurrently active substances) ----
function InteractionWarnings({ findings }) {
  if (!findings.length) return null;
  return (
    <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4" data-testid="interaction-warnings">
      <div className="flex gap-2 items-start">
        <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
        <p className="font-semibold text-sm">Interaction check</p>
      </div>
      <div className="mt-2 space-y-2">
        {findings.map((f, i) => {
          const meta = severityMeta(f.severity);
          return (
            <div key={i} className="rounded-xl bg-card/60 p-2.5" data-testid="interaction-finding">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <RiskBadge tone={meta.tone} label={meta.label} />
                <span className="text-xs font-medium">{f.aName} + {f.bName}</span>
              </div>
              <p className="text-xs text-muted-foreground">{f.reason}</p>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-muted-foreground mt-2">Harm-reduction heuristic based on drug category, not a clinical interaction database — when in doubt, ask a pharmacist or use a dedicated interaction checker.</p>
    </div>
  );
}

// Medications keep whatever color the user picked, but two meds can easily
// share the same (often default) swatch — illegible once their curves are
// overlaid on one chart. Keep colors that are already distinct; only
// reassign a fresh palette color to whichever sessions collide.
function distinctSessionColors(sessions) {
  const used = new Set();
  return sessions.map((s) => {
    let c = s.medication_color;
    if (used.has(c)) c = MED_COLORS.find((mc) => !used.has(mc)) || c;
    used.add(c);
    return c;
  });
}

// ---- combined chart (2+ concurrently active substances, one shared timeline) ----
function CombinedEffectsChart({ sessions, now }) {
  const colors = useMemo(() => distinctSessionColors(sessions), [sessions]);
  const anchor = useMemo(() => Math.min(...sessions.map((s) => new Date(s.started_at).getTime())), [sessions]);
  const offsets = useMemo(() => sessions.map((s) => (new Date(s.started_at).getTime() - anchor) / 60000), [sessions, anchor]);
  // Each session's own (possibly redosed) dose stack, evaluated on the shared
  // wall-clock timeline.
  const stacks = useMemo(() => sessions.map((s) => sessionDoseStack(s)), [sessions]);
  const chartEnd = useMemo(
    () => Math.max(60, ...sessions.map((s, i) => offsets[i] + stackChartEnd(s.profile, stacks[i]))),
    [sessions, offsets, stacks]
  );
  const hourStep = axisStepMin(chartEnd);
  const hourTicks = useMemo(() => {
    const out = [];
    for (let m = 0; m <= chartEnd; m += hourStep) out.push(m);
    return out;
  }, [chartEnd, hourStep]);
  const sampleStep = Math.max(5, Math.round(chartEnd / 96 / 5) * 5);
  const data = useMemo(() => {
    const points = [];
    for (let t = 0; t <= chartEnd; t += sampleStep) {
      const row = { t: Math.round(t) };
      sessions.forEach((s, i) => { row[s.id] = Math.round(stackedIntensityAt(t - offsets[i], s.profile, stacks[i])); });
      points.push(row);
    }
    return points;
  }, [sessions, offsets, stacks, chartEnd, sampleStep]);
  // Fit the y-axis to the tallest curve actually drawn (redoses/strong doses
  // can exceed 100%).
  const yMax = useMemo(() => {
    let mx = 100;
    for (const row of data) for (const s of sessions) mx = Math.max(mx, row[s.id] || 0);
    return Math.min(300, Math.ceil(mx / 25) * 25);
  }, [data, sessions]);
  const yTicks = useMemo(() => (yMax <= 100 ? [0, 25, 50, 75, 100] : Array.from(new Set([0, 50, 100, yMax])).sort((a, b) => a - b)), [yMax]);
  const clockAt = (mins) => fmtDate(new Date(anchor + mins * 60000), "h:mm a");
  const nowMin = (now - anchor) / 60000;

  return (
    <div className="card-soft p-4" data-testid="combined-effects-chart">
      <div className="flex items-center gap-2 mb-2">
        <Layers className="h-4 w-4 text-primary" />
        <p className="font-semibold text-sm">Combined view</p>
        <span className="text-xs text-muted-foreground">· {sessions.length} active</span>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1.5 mb-2">
        {sessions.map((s, i) => (
          <div key={s.id} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: colors[i] }} />
            {s.medication_name}
          </div>
        ))}
      </div>
      <div className="-mx-1">
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={data} margin={{ left: 0, right: 8, top: 6, bottom: 0 }}>
            <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.55} strokeDasharray="2 4" horizontalValues={yTicks.filter((v) => v > 0)} verticalValues={hourTicks} />
            <XAxis dataKey="t" type="number" domain={[0, chartEnd]} ticks={hourTicks} interval={0} tickFormatter={clockAt} tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
            <YAxis domain={[0, yMax + 5]} ticks={yTicks} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 9 }} width={38} tickMargin={4} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }}
              formatter={(v, name) => [`${Math.round(v)}%`, sessions.find((s) => s.id === name)?.medication_name || name]}
              labelFormatter={clockAt}
            />
            {sessions.map((s, i) => (
              <Area key={s.id} type="monotone" dataKey={s.id} name={s.id} stroke={colors[i]} strokeWidth={2.5} fill={colors[i]} fillOpacity={0.12} dot={false} isAnimationActive={false} />
            ))}
            {nowMin >= 0 && nowMin <= chartEnd && (
              <ReferenceLine x={Math.round(nowMin)} stroke="hsl(var(--warning))" strokeDasharray="4 3" strokeWidth={2} label={{ value: "now", position: "insideTopRight", fontSize: 9, fill: "hsl(var(--warning))" }} />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function ActiveEffectsDetail() {
  const now = useNow(30000);
  const qc = useQueryClient();
  const { data: sessions = [] } = useActiveSessions();
  const { data: logs = [] } = useQuery({ queryKey: ["logs"], queryFn: () => getLogs({ limit: 200 }) });
  const { data: meds = [] } = useQuery({ queryKey: ["medications"], queryFn: () => getMedications() });

  // Recent consuming doses (last 8 h) without an active session → offer to track,
  // backdated to when the dose was actually taken.
  // Doses that already have a session (of any status) must never be offered
  // again — otherwise finishing a session instantly re-offered "Track effects
  // of X" for the very dose just tracked, so the tracker never looked cleared.
  const { data: allSessions = [] } = useQuery({ queryKey: ["effectSessions", "all"], queryFn: () => getEffectSessions() });
  const trackedLogIds = useMemo(() => {
    const ids = new Set();
    for (const s of allSessions) {
      if (s.log_id) ids.add(s.log_id);
      for (const r of s.redoses || []) if (r.log_id) ids.add(r.log_id);
    }
    return ids;
  }, [allSessions]);

  const offers = useMemo(() => {
    const activeMedIds = new Set(sessions.map((s) => s.medication_id));
    const cutoff = now - 8 * 3600000;
    const seen = new Set();
    return logs.filter((l) => {
      if (!["taken", "partial"].includes(l.status)) return false;
      if (new Date(l.timestamp).getTime() < cutoff) return false;
      if (trackedLogIds.has(l.id)) return false;
      if (activeMedIds.has(l.medication_id) || seen.has(l.medication_id)) return false;
      seen.add(l.medication_id);
      return true;
    }).slice(0, 3);
  }, [logs, sessions, now, trackedLogIds]);

  const start = useMutation({
    mutationFn: (log) => startEffectSession({ medication_id: log.medication_id, dose: log.dose_taken, unit: log.unit, log_id: log.id, started_at: log.timestamp }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["effectSessions"] }); toast.success("Effects tracking started"); },
    onError: (e) => toast.error(e?.message || "Could not start tracking"),
  });

  // Cross-reference concurrently active substances against the interaction
  // matrix — this is where it matters in real time, not the user's whole
  // medication list, since risk depends on what's actually active together.
  const interactionFindings = useMemo(() => {
    const items = sessions.map((s) => {
      const m = meds.find((x) => x.id === s.medication_id);
      return { id: s.id, name: s.medication_name, generic_name: m?.generic_name, category: m?.category };
    });
    return checkInteractions(items);
  }, [sessions, meds]);

  if (!sessions.length && !offers.length) return null;
  const medName = (id) => meds.find((m) => m.id === id)?.name || "Medication";

  return (
    <div className="space-y-3" data-testid="active-effects-detail">
      <p className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" />Active effects</p>
      <InteractionWarnings findings={interactionFindings} />
      {sessions.length > 1 && <CombinedEffectsChart sessions={sessions} now={now} />}
      {sessions.map((s) => <SessionDetail key={s.id} session={s} now={now} />)}
      {offers.map((l) => (
        <button key={l.id} onClick={() => start.mutate(l)} data-testid="effect-track-offer" className="w-full text-left card-soft p-3 pressable border-dashed">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/12 text-primary flex items-center justify-center"><Play className="h-5 w-5" /></div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">Track effects of {medName(l.medication_id)}</p>
              <p className="text-xs text-muted-foreground">Taken {relativeTime(l.timestamp)}{l.dose_taken != null ? ` · ${doseLabel(l.dose_taken, l.unit)}` : ""}</p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </div>
        </button>
      ))}
    </div>
  );
}
